import { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { X, ArrowRight, Sparkles } from 'lucide-react';

/**
 * FirstCallTour
 *
 * A 5-step contextual tour that appears the FIRST time a user starts a call.
 * It dims the rest of the page, highlights one anchor at a time via a
 * spotlight cut-out, and shows a tooltip card with title/body/action.
 *
 * Anchors are looked up by `data-testid` so the tour stays decoupled from
 * markup. Dismissal is persisted to localStorage so the tour never reappears.
 *
 * Props:
 *   - active: boolean — when true the tour renders
 *   - steps: Array<{ testId?: string, title: string, body: string, placement?: 'bottom'|'top'|'left'|'right' }>
 *   - storageKey: string — localStorage key used to remember dismissal
 *   - onComplete: () => void — fired after the final step or skip
 */
export function FirstCallTour({ active, steps, storageKey = 'pg_first_call_tour_done', onComplete }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[idx];

  // Recompute the spotlight rect when the active step changes OR on resize.
  // Re-running on a short interval handles cases where the anchor is rendered
  // a tick after the step advances (e.g. transcript toggle).
  useEffect(() => {
    if (!active || !step) return;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      if (!step.testId) { setRect(null); return; }
      const el = document.querySelector(`[data-testid="${step.testId}"]`);
      if (el) setRect(el.getBoundingClientRect());
      else setRect(null);
    };
    measure();
    const interval = setInterval(measure, 250);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, step, idx]);

  const finish = useCallback(() => {
    try { localStorage.setItem(storageKey, '1'); } catch (_) { /* ignore storage errors (private mode, etc.) */ }
    if (onComplete) onComplete();
  }, [storageKey, onComplete]);

  if (!active || !step) return null;

  const isLast = idx === steps.length - 1;
  const next = () => isLast ? finish() : setIdx(idx + 1);

  // Spotlight halo around the anchor (12px padding).
  const halo = rect ? {
    top: rect.top - 12,
    left: rect.left - 12,
    width: rect.width + 24,
    height: rect.height + 24,
  } : null;

  // Tooltip placement: prefer below the anchor, fall back to above if no room.
  const PADDING = 14;
  const tipTop = halo
    ? (halo.top + halo.height + PADDING + 220 < window.innerHeight
       ? halo.top + halo.height + PADDING
       : halo.top - 220 - PADDING)
    : Math.max(80, window.innerHeight / 2 - 110);
  const tipLeft = halo
    ? Math.min(window.innerWidth - 380, Math.max(16, halo.left + halo.width / 2 - 170))
    : Math.max(16, window.innerWidth / 2 - 170);

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none" data-testid="first-call-tour">
      {/* Dim layer with spotlight cut-out. Using four rects around the halo
          keeps the anchor fully clickable while everything else is dimmed. */}
      {halo ? (
        <>
          <div className="fixed bg-black/55 pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: halo.top }} />
          <div className="fixed bg-black/55 pointer-events-auto" style={{ top: halo.top + halo.height, left: 0, right: 0, bottom: 0 }} />
          <div className="fixed bg-black/55 pointer-events-auto" style={{ top: halo.top, left: 0, width: halo.left, height: halo.height }} />
          <div className="fixed bg-black/55 pointer-events-auto" style={{ top: halo.top, left: halo.left + halo.width, right: 0, height: halo.height }} />
          {/* Pulsing ring around the anchor */}
          <div
            className="fixed rounded-lg ring-2 ring-brand-primary animate-pulse pointer-events-none"
            style={{ top: halo.top, left: halo.left, width: halo.width, height: halo.height }}
          />
        </>
      ) : (
        <div className="fixed inset-0 bg-black/55 pointer-events-auto" />
      )}

      {/* Tooltip card */}
      <div
        className="fixed w-[340px] bg-white rounded-lg shadow-2xl p-5 pointer-events-auto"
        style={{ top: tipTop, left: tipLeft }}
        data-testid={`tour-step-${idx}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-brand-accent-muted flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-brand-primary" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-primary">
              Step {idx + 1} of {steps.length}
            </span>
          </div>
          <button
            onClick={finish}
            className="text-zinc-400 hover:text-zinc-700 transition-colors"
            data-testid="tour-skip-btn"
            aria-label="Skip tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-base font-semibold text-zinc-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
          {step.title}
        </h3>
        <p className="text-sm text-zinc-600 leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <button
            onClick={finish}
            className="text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
            data-testid="tour-skip-link"
          >
            Skip tour
          </button>
          <Button onClick={next} size="sm" className="bg-brand-primary hover:bg-brand-hover" data-testid="tour-next-btn">
            {isLast ? 'Got it' : 'Next'}
            {!isLast && <ArrowRight className="w-3.5 h-3.5 ml-1.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
